import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { H1, Text } from '../components/ui/Typography';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import { translations } from '../translations';

const Contact = () => {
  const { lang = 'nl' } = useParams();
  const t = translations[lang]?.contact || translations.nl.contact;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    website: '' 
  });
  
  const [status, setStatus] = useState('idle'); 
  const [errorMessage, setErrorMessage] = useState('');

  const validateEmail = (email) => {
    return String(email).toLowerCase().match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check (Spam preventie)
    if (formData.website) { 
      setStatus('success'); 
      return; 
    }

    const submissionData = {
      first_name: formData.firstName.trim(),
      last_name: formData.lastName.trim(),
      email: formData.email.trim(),
      message: formData.message.trim()
    };

    // 1. Basis Validatie
    if (!submissionData.first_name || !submissionData.last_name || !submissionData.email || !submissionData.message) {
      setStatus('error');
      setErrorMessage("Vul alstublieft alle verplichte velden in.");
      return;
    }

    if (!validateEmail(submissionData.email)) {
      setStatus('error');
      setErrorMessage("Vul een geldig e-mailadres in.");
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      // 2. STAP 1: E-MAIL VERZENDEN (Edge Function aanroepen)
      // We doen dit eerst omdat de e-mail de hoogste prioriteit heeft.
      const { data, error: fnError } = await supabase.functions.invoke('send-contact-email', {
        body: submissionData
      });

      // Foutafhandeling voor Edge Function
      if (fnError || (data && data.success === false)) {
        console.error('Email verzendfout:', fnError || data?.error);
        throw new Error(data?.error || "E-mail verzending mislukt. Probeer het later opnieuw.");
      }

      // 3. STAP 2: OPSLAAN IN DATABASE (Archief)
      // We slaan het nu pas op omdat we weten dat de e-mail geaccepteerd is.
      const { error: dbError } = await supabase
        .from('contact_messages')
        .insert([{ 
          ...submissionData,
          metadata: { 
            source: 'web_form', 
            lang, 
            email_sent: true,
            resend_id: data?.id
          }
        }]);
      
      if (dbError) {
        // We tonen nog steeds succes aan de gebruiker omdat de e-mail weg is,
        // maar we loggen de database fout in de console.
        console.warn('E-mail verzonden, maar kon niet worden opgeslagen in database:', dbError.message);
      }

      // Volledig succes
      setStatus('success');
      setFormData({ firstName: '', lastName: '', email: '', message: '', website: '' });
      localStorage.setItem('last_contact_submission', Date.now().toString());
      
    } catch (err) {
      console.error('Contact formulier flow onderbroken:', err.message);
      setStatus('error');
      setErrorMessage(err.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (status === 'error') setStatus('idle');
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 md:py-20 lg:py-24">
      <div className="grid lg:grid-cols-2 gap-16 md:gap-20 items-start">
        <div className="space-y-8 md:space-y-10 lg:pt-2">
          <div className="space-y-5">
            <H1 className="text-navy-950 tracking-tight leading-[1.1] mb-0">{t.title}</H1>
            <div className="w-12 h-1 bg-navy-900/10 rounded-full" />
            <Text className="text-slate-500 text-lg leading-relaxed max-w-lg">
              {t.intro}
            </Text>
          </div>

          <div className="flex gap-6 items-start group">
            <div className="w-11 h-11 bg-slate-50 rounded-2xl flex items-center justify-center text-navy-900 shrink-0 border border-slate-100 group-hover:bg-navy-900 group-hover:text-white transition-all duration-300">
              <SafeIcon name="Mail" className="text-base" />
            </div>
            <div>
              <h4 className="font-bold text-navy-900 text-lg mb-1">{t.emailTitle}</h4>
              <p className="text-slate-500 text-sm mb-2 leading-relaxed">
                {t.emailDesc}
              </p>
              <span className="text-navy-900 font-bold border-b-2 border-navy-900/10 pb-0.5">
                testamentair@proton.me
              </span>
            </div>
          </div>
        </div>

        <div>
          <Card className="p-8 md:p-9 border-slate-100 shadow-xl bg-white rounded-[2rem] relative overflow-hidden" hover={false}>
            <AnimatePresence mode="wait">
              {status === 'success' ? (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-12 text-center space-y-6"
                >
                  <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto border border-emerald-100">
                    <SafeIcon name="Check" className="text-2xl" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-bold text-navy-900">Bericht verzonden</h3>
                    <p className="text-slate-500 text-sm max-w-xs mx-auto leading-relaxed">
                      {t.success}
                    </p>
                  </div>
                  <Button variant="secondary" onClick={() => setStatus('idle')} className="mt-4">
                    Nieuw bericht
                  </Button>
                </motion.div>
              ) : (
                <motion.div key="form" exit={{ opacity: 0, y: -10 }}>
                  <h3 className="text-xl font-bold text-navy-900 mb-7">{t.formTitle}</h3>
                  <form className="space-y-4" onSubmit={handleSubmit}>
                    {/* Honeypot field */}
                    <div className="hidden" aria-hidden="true">
                      <input type="text" name="website" tabIndex="-1" value={formData.website} onChange={handleChange} autoComplete="off" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">{t.firstName}</label>
                        <input type="text" name="firstName" required disabled={status === 'loading'} value={formData.firstName} onChange={handleChange} className="w-full px-5 py-3.5 rounded-xl border border-slate-100 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:bg-white focus:border-navy-900 transition-all duration-200 text-navy-950 font-medium placeholder:text-slate-300 disabled:opacity-50" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">{t.lastName}</label>
                        <input type="text" name="lastName" required disabled={status === 'loading'} value={formData.lastName} onChange={handleChange} className="w-full px-5 py-3.5 rounded-xl border border-slate-100 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:bg-white focus:border-navy-900 transition-all duration-200 text-navy-950 font-medium placeholder:text-slate-300 disabled:opacity-50" />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">{t.email}</label>
                      <input type="email" name="email" required disabled={status === 'loading'} value={formData.email} onChange={handleChange} className="w-full px-5 py-3.5 rounded-xl border border-slate-100 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:bg-white focus:border-navy-900 transition-all duration-200 text-navy-950 font-medium placeholder:text-slate-300 disabled:opacity-50" />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">{t.message}</label>
                      <textarea rows="4" name="message" required disabled={status === 'loading'} value={formData.message} onChange={handleChange} className="w-full px-5 py-3.5 rounded-xl border border-slate-100 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:bg-white focus:border-navy-900 transition-all duration-200 text-navy-950 font-medium placeholder:text-slate-300 resize-none disabled:opacity-50" />
                    </div>

                    {status === 'error' && (
                      <div className="bg-red-50 border border-red-100 p-4 rounded-xl flex items-start gap-3">
                        <SafeIcon name="AlertCircle" className="text-red-500 shrink-0 mt-0.5" />
                        <p className="text-red-700 text-xs font-bold leading-relaxed">{errorMessage}</p>
                      </div>
                    )}

                    <div className="pt-4 text-center">
                      <Button type="submit" className="w-full py-4 text-base shadow-lg flex items-center justify-center gap-2" disabled={status === 'loading'}>
                        {status === 'loading' && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                        {status === 'loading' ? t.submitting : t.button}
                      </Button>
                      <p className="mt-6 text-[9px] text-slate-400 font-medium tracking-wide opacity-70 uppercase">
                        {t.disclaimer}
                      </p>
                    </div>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Contact;